import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Radio, ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneField } from "@/components/PhoneField";
import { Checkbox } from "@/components/ui/checkbox";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone_number: "", phone_country: "", age_confirmed: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [signupsOpen, setSignupsOpen] = useState(null);

  useEffect(() => {
    api.get("/auth/config").then(({ data }) => setSignupsOpen(data.signups_open !== false)).catch(() => setSignupsOpen(true));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (signupsOpen === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-8">
        <div className="w-full max-w-md animate-fade-up text-center" data-testid="register-closed">
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="w-9 h-9 bg-[#007AFF] flex items-center justify-center">
              <Radio className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-black text-xl tracking-tighter">Incident Tracking &amp; Inquiry Services</span>
          </div>
          <div className="inline-flex items-center gap-2 border border-[#FFCC00]/40 bg-[#FFCC00]/10 text-[#FFCC00] rounded-sm px-4 py-2 mb-6">
            <Lock className="w-4 h-4" />
            <span className="text-sm font-semibold">Private beta</span>
          </div>
          <h2 className="font-heading text-3xl font-black tracking-tighter mb-3">Sign-ups are closed right now</h2>
          <p className="text-sm text-muted-foreground mb-8">
            We're not accepting new accounts at the moment. Please check back soon — thanks for your interest.
          </p>
          <Link to="/login" data-testid="closed-go-login" className="text-[#007AFF] hover:underline text-sm font-semibold">
            Already have access? Sign in
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.age_confirmed) {
      setError("You must confirm that you are 18 years of age or older to create an account.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <form onSubmit={submit} className="w-full max-w-md animate-fade-up" data-testid="register-form">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 bg-[#007AFF] flex items-center justify-center">
            <Radio className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-black text-xl tracking-tighter">Incident Tracking &amp; Inquiry Services</span>
        </div>

        <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Get started</p>
        <h2 className="font-heading text-3xl font-black tracking-tighter mb-2">Create account</h2>
        <p className="text-sm text-muted-foreground mb-8">
          Add your phone number so incoming voice reports from that caller ID are filed to your account automatically.
          New accounts start with <span className="text-white font-semibold">$5.05 in free trial credits</span>.
        </p>

        {error && (
          <div className="mb-4 border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] text-sm px-3 py-2" data-testid="register-error">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Full name</Label>
            <Input data-testid="register-name" value={form.name} onChange={set("name")}
              className="mt-2 bg-[#0A0A0A] border-border rounded-sm focus-visible:ring-[#007AFF]" placeholder="Jane Doe" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Email</Label>
            <Input data-testid="register-email" type="email" required value={form.email} onChange={set("email")}
              className="mt-2 bg-[#0A0A0A] border-border rounded-sm focus-visible:ring-[#007AFF]" placeholder="you@company.com" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Phone number (caller ID)</Label>
            <div className="mt-2">
              <PhoneField value={form.phone_number} testidPrefix="register-phone"
                onChange={(e164, dial) => setForm((f) => ({ ...f, phone_number: e164, phone_country: dial }))} />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Password</Label>
            <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")}
              className="mt-2 bg-[#0A0A0A] border-border rounded-sm focus-visible:ring-[#007AFF]" placeholder="min. 6 characters" />
          </div>
          <label className="flex items-start gap-3 cursor-pointer border border-border bg-[#0A0A0A] rounded-sm px-3 py-3" data-testid="register-age-label">
            <Checkbox checked={form.age_confirmed} onCheckedChange={(v) => setForm((f) => ({ ...f, age_confirmed: Boolean(v) }))}
              data-testid="register-age-checkbox" className="mt-0.5" />
            <span className="text-sm text-muted-foreground">
              <span className="text-white font-semibold">I confirm I am 18 years of age or older.</span>
              <span className="block text-xs mt-1">This is an adult-only service. Minors are prohibited from using this site.</span>
            </span>
          </label>
        </div>

        <Button type="submit" disabled={loading} data-testid="register-submit"
          className="w-full mt-6 bg-[#007AFF] hover:bg-[#0066d6] text-white rounded-sm gap-2 font-semibold h-11">
          {loading ? "Creating…" : "Create account"} <ArrowRight className="w-4 h-4" />
        </Button>

        <p className="text-sm text-muted-foreground mt-6">
          Already have an account?{" "}
          <Link to="/login" data-testid="go-login" className="text-[#007AFF] hover:underline">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
